import React, { DragEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PublicIcon from '@mui/icons-material/Public';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { signedFetch } from '../super-picker/services/api';
import SidebarNavigation, { NavigationGroup } from '../super-picker/components/sidebar/SidebarNavigation';
import AppHeader from '../super-picker/components/header/AppHeader';
import { useTheme } from '../theme/ThemeProvider';
import { getPhotoUrl } from '../utils/formatting';

const EXPECTED_HEADERS = [
  'Project Name',
  'Apartment',
  'Condominium',
  'Detached House',
  'Executive Condominium',
  'Semi-Detached House',
  'Terrace House',
  'Total',
] as const;

type UnitsRow = {
  projectName: string;
  apartment: number;
  condominium: number;
  detachedHouse: number;
  executiveCondominium: number;
  semiDetachedHouse: number;
  terraceHouse: number;
  total: number;
};

type ValidationResult = {
  kind: 'units';
  rows: UnitsRow[];
  normalizedRecords: number;
  totalUnits: number;
};

const NATIONALITY_ROW_LABELS = [
  'Nationality by Residential Status',
  'Company',
  'Foreigner (NPR)',
  'Singapore Permanent Residents (PR)',
  'Singaporean',
  'Total',
] as const;

type NationalityRow = {
  projectName: string;
  company: number;
  foreigner: number;
  permanentResident: number;
  singaporean: number;
  total: number;
};

type NationalityValidationResult = {
  kind: 'nationality';
  year: number;
  rows: NationalityRow[];
  normalizedRecords: number;
  totalBuyers: number;
};

type CsvValidationResult = ValidationResult | NationalityValidationResult;
type DataImportResult = {
  imported: number;
  inserted: number;
  updated: number;
  totalUnits?: number;
  totalBuyers?: number;
  year?: number;
  skipped: number;
  skippedProjects: string[];
  missingHuttonsAppProjectIds?: number;
  missingHuttonsPortalProjectIds?: number;
  missingBothProjectIds?: number;
};

const rawApiUrl = process.env.REACT_APP_API_URL || '/';
const apiRoot = rawApiUrl.replace(/\/+$/, '');
const useHostingProxy = apiRoot === '' || apiRoot === '/';
const functionBase = useHostingProxy ? '' : (apiRoot.endsWith('/api') ? apiRoot : `${apiRoot}/api`);
const apiBase = useHostingProxy ? '/api' : `${functionBase}/api`;

function parseCount(value: unknown, rowNumber: number, column: string): number {
  const text = String(value ?? '').trim();
  if (text === '-') return 0;
  if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)$/.test(text)) {
    throw new Error(`Row ${rowNumber}: “${column}” must be a whole number or “-”.`);
  }
  const result = Number(text.replace(/,/g, ''));
  if (!Number.isSafeInteger(result)) throw new Error(`Row ${rowNumber}: “${column}” is too large.`);
  return result;
}

async function validateUnitsCsv(file: File): Promise<ValidationResult> {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    throw new Error('This is not a CSV file. You may be on the wrong upload page or have selected the wrong file.');
  }

  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', raw: false });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const data = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: false, defval: '' });
  const headers = (data[0] || []).map((value) => String(value).replace(/^\uFEFF/, '').trim());
  const validHeaders = headers.length === EXPECTED_HEADERS.length
    && EXPECTED_HEADERS.every((header, index) => headers[index] === header);
  if (!validHeaders) {
    throw new Error(`This file does not match the Units format. You may be on the wrong upload page or have selected the wrong file. Expected: ${EXPECTED_HEADERS.join(', ')}.`);
  }
  if (data.length < 2) throw new Error('The selected Units CSV contains no data rows.');

  const dataRows = data.slice(1).filter((row) => row.some((value) => String(value).trim()));
  const finalProjectName = String(dataRows[dataRows.length - 1]?.[0] ?? '').trim().toLocaleUpperCase('en-SG');
  if (finalProjectName === 'TOTAL') dataRows.pop();

  const seenProjects = new Set<string>();
  let normalizedRecords = 0;
  let totalUnits = 0;
  const rows = dataRows.map((values, index) => {
    const rowNumber = index + 2;
    if (values.length !== EXPECTED_HEADERS.length) {
      throw new Error(`Row ${rowNumber}: expected ${EXPECTED_HEADERS.length} columns but found ${values.length}.`);
    }
    const projectName = String(values[0]).trim();
    if (!projectName) throw new Error(`Row ${rowNumber}: Project Name is required.`);
    const projectKey = projectName.toLocaleUpperCase('en-SG');
    if (seenProjects.has(projectKey)) throw new Error(`Row ${rowNumber}: duplicate project “${projectName}”.`);
    seenProjects.add(projectKey);

    const counts = EXPECTED_HEADERS.slice(1, -1).map((header, offset) =>
      parseCount(values[offset + 1], rowNumber, header),
    );
    const total = parseCount(values[7], rowNumber, 'Total');
    const calculatedTotal = counts.reduce((sum, count) => sum + count, 0);
    if (calculatedTotal !== total) {
      throw new Error(`Row ${rowNumber}: Total is ${total.toLocaleString()}, but the property types add up to ${calculatedTotal.toLocaleString()}.`);
    }
    normalizedRecords += counts.filter((count) => count > 0).length;
    totalUnits += total;
    return {
      projectName,
      apartment: counts[0],
      condominium: counts[1],
      detachedHouse: counts[2],
      executiveCondominium: counts[3],
      semiDetachedHouse: counts[4],
      terraceHouse: counts[5],
      total,
    };
  });

  if (rows.length === 0) throw new Error('The selected Units CSV contains no data rows.');
  return { kind: 'units', rows, normalizedRecords, totalUnits };
}

async function validateNationalityCsv(file: File, selectedYear: string): Promise<NationalityValidationResult> {
  if (!selectedYear) throw new Error('Select the data year before choosing a Nationality CSV.');
  if (!file.name.toLowerCase().endsWith('.csv')) {
    throw new Error('This is not a CSV file. You may be on the wrong upload page or have selected the wrong file.');
  }
  const yearInFileName = file.name.match(/(?:19|20)\d{2}/)?.[0];
  if (yearInFileName && yearInFileName !== selectedYear) {
    throw new Error(`The selected year is ${selectedYear}, but the filename appears to be for ${yearInFileName}. Please select the correct year or file.`);
  }
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', raw: false });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const data = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: false, defval: '' });
  if (data.length !== NATIONALITY_ROW_LABELS.length) {
    throw new Error('This file does not match the Nationality format. You may be on the wrong upload page or have selected the wrong file.');
  }
  const width = data[0]?.length || 0;
  if (width < 3 || data.some((row) => row.length !== width)) {
    throw new Error('The Nationality CSV must be a complete rectangular table with project columns and a Total column.');
  }
  NATIONALITY_ROW_LABELS.forEach((label, index) => {
    const actual = String(data[index]?.[0] ?? '').replace(/^\uFEFF/, '').trim();
    if (actual !== label) throw new Error(`Expected row ${index + 1} to start with “${label}”, but found “${actual || 'blank'}”.`);
  });
  if (String(data[0][width - 1]).trim() !== 'Total') {
    throw new Error('The final column in the Nationality CSV must be “Total”.');
  }

  const projectRowIndex = new Map<string, number>();
  let normalizedRecords = 0;
  const rows: NationalityRow[] = [];
  const rawStatusSums = [0, 0, 0, 0];
  let rawGrandTotal = 0;
  for (let column = 1; column < width - 1; column += 1) {
    const projectName = String(data[0][column]).trim();
    if (!projectName) throw new Error(`Column ${column + 1}: project name is required.`);
    const projectKey = projectName.replace(/\s+/g, ' ').toLocaleUpperCase('en-SG');
    const counts = NATIONALITY_ROW_LABELS.slice(1, -1).map((label, offset) =>
      parseCount(data[offset + 1][column], offset + 2, `${projectName} — ${label}`),
    );
    const total = parseCount(data[5][column], 6, `${projectName} — Total`);
    const calculatedTotal = counts.reduce((sum, count) => sum + count, 0);
    if (calculatedTotal !== total) {
      throw new Error(`Project “${projectName}”: Total is ${total.toLocaleString()}, but its residential statuses add up to ${calculatedTotal.toLocaleString()}.`);
    }
    counts.forEach((count, index) => { rawStatusSums[index] += count; });
    rawGrandTotal += total;
    const latestRow: NationalityRow = {
      projectName,
      company: counts[0],
      foreigner: counts[1],
      permanentResident: counts[2],
      singaporean: counts[3],
      total,
    };
    const existingIndex = projectRowIndex.get(projectKey);
    if (existingIndex === undefined) {
      projectRowIndex.set(projectKey, rows.length);
      rows.push(latestRow);
    } else {
      rows[existingIndex] = latestRow;
    }
  }
  normalizedRecords = rows.reduce(
    (sum, row) => sum + [row.company, row.foreigner, row.permanentResident, row.singaporean].filter((count) => count > 0).length,
    0,
  );

  const statusTotals = NATIONALITY_ROW_LABELS.slice(1, -1).map((label, offset) =>
    parseCount(data[offset + 1][width - 1], offset + 2, `${label} total`),
  );
  statusTotals.forEach((expected, statusIndex) => {
    const actual = rawStatusSums[statusIndex];
    if (actual !== expected) throw new Error(`${NATIONALITY_ROW_LABELS[statusIndex + 1]} total is ${expected.toLocaleString()}, but the project columns add up to ${actual.toLocaleString()}.`);
  });
  const suppliedGrandTotal = parseCount(data[5][width - 1], 6, 'Grand Total');
  if (rawGrandTotal !== suppliedGrandTotal || statusTotals.reduce((sum, count) => sum + count, 0) !== suppliedGrandTotal) {
    throw new Error(`Grand Total is ${suppliedGrandTotal.toLocaleString()}, but the Nationality data does not add up to that value.`);
  }
  const totalBuyers = rows.reduce((sum, row) => sum + row.total, 0);
  return { kind: 'nationality', year: Number(selectedYear), rows, normalizedRecords, totalBuyers };
}

async function importUnits(rows: UnitsRow[]): Promise<DataImportResult> {
  const response = await signedFetch(`${apiBase}/data-dashboard/units/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const details = [
      ...(Array.isArray(payload?.unmatchedProjects) ? payload.unmatchedProjects : []),
      ...(Array.isArray(payload?.ambiguousProjects) ? payload.ambiguousProjects : []),
    ];
    const suffix = details.length > 0 ? ` Projects requiring attention: ${details.join(', ')}.` : '';
    throw new Error(`${payload?.error || 'Failed to import Units data.'}${suffix}`);
  }
  return payload as DataImportResult;
}

async function importNationality(year: number, rows: NationalityRow[]): Promise<DataImportResult> {
  const response = await signedFetch(`${apiBase}/data-dashboard/nationality/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ year, rows }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const details = Array.isArray(payload?.unmatchedProjects) ? payload.unmatchedProjects : [];
    const suffix = details.length > 0 ? ` Projects requiring attention: ${details.join(', ')}.` : '';
    throw new Error(`${payload?.error || 'Failed to import Nationality data.'}${suffix}`);
  }
  return payload as DataImportResult;
}

export default function DataDashboardApp() {
  const { currentTheme, setTheme } = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [access, setAccess] = useState<'checking' | 'granted' | 'denied'>('checking');
  const [activeDataset, setActiveDataset] = useState<'units' | 'nationality'>('units');
  const [selectedYear, setSelectedYear] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [validation, setValidation] = useState<CsvValidationResult | null>(null);
  const [importResult, setImportResult] = useState<DataImportResult | null>(null);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loginUser, setLoginUser] = useState<{ name: string; photo?: string; role: string } | null>(null);

  const navigationGroups: NavigationGroup[] = useMemo(() => [{
    id: 'data-management',
    label: 'Data Management',
    items: [
      { id: 'units', label: 'Units', icon: <DatasetOutlinedIcon fontSize="small" />, active: activeDataset === 'units', onClick: () => changeDataset('units') },
      { id: 'nationality', label: 'Nationality', icon: <PublicIcon fontSize="small" />, active: activeDataset === 'nationality', onClick: () => changeDataset('nationality') },
    ],
  }], [activeDataset]);

  useEffect(() => {
    let mounted = true;
    signedFetch(`${apiBase}/data-dashboard/access`)
      .then(async (response) => {
        if (!response.ok) throw new Error('Unable to verify Data Dashboard access.');
        const payload = await response.json();
        if (mounted) {
          setLoginUser({
            name: String(payload?.user?.Name || payload?.user?.name || 'Administrator'),
            photo: getPhotoUrl(payload?.user?.Photo || payload?.user?.photo),
            role: 'Admin',
          });
          setAccess('granted');
        }
      })
      .catch(() => {
        if (mounted) {
          setAccess('denied');
          navigate('/super-picker', { replace: true });
        }
      });
    return () => { mounted = false; };
  }, [navigate]);

  const clearFile = () => {
    setFile(null);
    setValidation(null);
    setImportResult(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  function changeDataset(dataset: 'units' | 'nationality') {
    setActiveDataset(dataset);
    setSelectedYear('');
    clearFile();
  }

  const selectFile = async (selected?: File) => {
    if (!selected) return;
    setFile(selected);
    setValidation(null);
    setImportResult(null);
    setError('');
    setValidating(true);
    try {
      setValidation(activeDataset === 'units'
        ? await validateUnitsCsv(selected)
        : await validateNationalityCsv(selected, selectedYear));
    } catch (validationError) {
      setError(validationError instanceof Error ? validationError.message : 'The selected file is not a valid Units CSV.');
    } finally {
      setValidating(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    void selectFile(event.dataTransfer.files[0]);
  };

  const handleImport = async () => {
    if (!validation) return;
    setImporting(true);
    setError('');
    setImportResult(null);
    try {
      setImportResult(validation.kind === 'units'
        ? await importUnits(validation.rows)
        : await importNationality(validation.year, validation.rows));
    } catch (importError) {
      setError(importError instanceof Error ? importError.message : `Failed to import ${activeDataset === 'units' ? 'Units' : 'Nationality'} data.`);
    } finally {
      setImporting(false);
    }
  };

  if (access === 'checking') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'var(--color-bg)' }}>
        <Stack alignItems="center" spacing={1.5}><CircularProgress /><Typography color="text.secondary">Checking Data Dashboard access…</Typography></Stack>
      </Box>
    );
  }

  if (access === 'denied') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'var(--color-bg)', p: 2 }}>
        <Alert icon={<LockOutlinedIcon />} severity="error" sx={{ maxWidth: 520 }}>
          This dashboard requires both administrator permission and an active Data Dashboard access grant.
        </Alert>
      </Box>
    );
  }

  const isDark = currentTheme === 'dark';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'var(--color-bg)', color: 'var(--color-text)', overflow: 'hidden' }}>
      <AppHeader
        currentTheme={currentTheme}
        userInfo={loginUser ? { Name: loginUser.name, Photo: loginUser.photo } : null}
        sidebarOpen={sidebarOpen}
        onSidebarToggle={() => setSidebarOpen((open) => !open)}
        onThemeToggle={() => setTheme(currentTheme === 'dark' ? 'light' : 'dark')}
        appLabel="Data Dashboard"
      />

      <Box sx={{ flex: 1, display: 'flex', mt: '64px', height: 'calc(100vh - 64px)', minHeight: 0, overflow: 'hidden' }}>
        <SidebarNavigation
          groups={navigationGroups}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onToggle={() => setSidebarOpen((open) => !open)}
          showMobileToggle={false}
        />
        <Box component="main" sx={{ flex: 1, minWidth: 0, overflow: 'auto', py: { xs: 3, md: 4 } }}>
          <Container maxWidth="lg">
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--color-heading)' }}>Data Dashboard</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>Manage the source files used for reporting.</Typography>
          </Box>
          <Chip icon={<LockOutlinedIcon />} label="Admin only" color="primary" variant="outlined" sx={{ alignSelf: 'flex-start' }} />
        </Stack>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={activeDataset}
            onChange={(_, value: 'units' | 'nationality') => changeDataset(value)}
            sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab icon={<DatasetOutlinedIcon />} iconPosition="start" value="units" label="Units" />
            <Tab icon={<PublicIcon />} iconPosition="start" value="nationality" label="Nationality" />
          </Tabs>

          <Card elevation={0} sx={{ borderRadius: 0 }}>
            <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {activeDataset === 'units' ? 'Upload project unit counts' : 'Upload project nationality data'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Select the {activeDataset === 'units' ? 'Units' : 'Nationality'} CSV. It will be checked locally before it can be imported.
              </Typography>

              {activeDataset === 'nationality' && (
                <FormControl size="small" sx={{ mt: 2, minWidth: 180 }} required>
                  <InputLabel id="nationality-year-label">Data year</InputLabel>
                  <Select
                    labelId="nationality-year-label"
                    value={selectedYear}
                    label="Data year"
                    onChange={(event) => {
                      setSelectedYear(event.target.value);
                      clearFile();
                    }}
                  >
                    {Array.from({ length: 7 }, (_, index) => String(2026 - index)).map((year) => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={() => setDragging(true)}
                onDragLeave={() => setDragging(false)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleDrop}
                sx={{
                  mt: 3,
                  p: { xs: 3, md: 5 },
                  textAlign: 'center',
                  cursor: 'pointer',
                  border: '2px dashed',
                  borderColor: dragging
                    ? 'primary.main'
                    : validation
                      ? 'success.main'
                      : isDark
                        ? 'rgba(255,255,255,0.7)'
                        : 'divider',
                  bgcolor: dragging
                    ? isDark ? 'rgba(237,137,54,0.12)' : 'rgba(237,137,54,0.07)'
                    : isDark ? 'rgba(255,255,255,0.035)' : '#fafbfe',
                  borderRadius: 2,
                  transition: 'background-color 150ms ease, border-color 150ms ease',
                  '&:hover': {
                    bgcolor: isDark ? 'rgba(255,255,255,0.065)' : '#f4f6fb',
                    borderColor: validation ? 'success.main' : 'primary.main',
                  },
                }}
              >
                <input ref={fileInputRef} hidden type="file" accept=".csv,text/csv" onChange={(event) => void selectFile(event.target.files?.[0])} />
                <CloudUploadOutlinedIcon color="primary" sx={{ fontSize: 48 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {activeDataset === 'units'
                    ? 'Upload the latest property unit data to validate and prepare it for import.'
                    : 'Upload the latest nationality by residential status data to validate and prepare it for import.'}
                </Typography>
                <Typography sx={{ mt: 1.5, fontWeight: 700 }}>
                  Drop your {activeDataset === 'units' ? 'Units' : 'Nationality'} CSV here
                </Typography>
                <Typography variant="body2" color="text.secondary">or click to select a file</Typography>
              </Box>

              {file && (
                <Paper variant="outlined" sx={{ mt: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <InsertDriveFileOutlinedIcon color="action" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{file.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{Math.max(1, Math.ceil(file.size / 1024)).toLocaleString()} KB</Typography>
                  </Box>
                  {validating && <CircularProgress size={22} />}
                  <Button size="small" color="inherit" startIcon={<DeleteOutlineIcon />} onClick={(event) => { event.stopPropagation(); clearFile(); }}>Remove</Button>
                </Paper>
              )}

              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
              {importResult && (
                <Alert
                  severity={(importResult.missingHuttonsAppProjectIds || importResult.missingHuttonsPortalProjectIds) ? 'warning' : 'success'}
                  sx={{ mt: 2 }}
                >
                  Processed {importResult.imported.toLocaleString()} project rows{importResult.year ? ` for ${importResult.year}` : ''}: {importResult.inserted.toLocaleString()} inserted and {importResult.updated.toLocaleString()} updated.
                  {(importResult.missingHuttonsAppProjectIds || importResult.missingHuttonsPortalProjectIds) ? (
                    <> All projects were imported. {(importResult.missingHuttonsAppProjectIds ?? 0).toLocaleString()} have no HuttonsApp project ID and {(importResult.missingHuttonsPortalProjectIds ?? 0).toLocaleString()} have no HuttonsPortal project ID; those values were stored as NULL. Of these, {(importResult.missingBothProjectIds ?? 0).toLocaleString()} project rows have no project ID from either table.</>
                  ) : null}
                </Alert>
              )}

              {validation && (
                <Box sx={{ mt: 3 }}>
                  <Alert icon={<CheckCircleOutlineIcon />} severity="success">
                    This file matches the {validation.kind === 'units' ? 'Units' : 'Nationality'} format and is ready for import.
                  </Alert>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ my: 2 }}>
                    {validation.kind === 'nationality' && <Chip color="primary" variant="outlined" label={`Year ${validation.year}`} />}
                    <Chip label={`${validation.rows.length.toLocaleString()} total project rows`} />
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview — first 5 projects</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      {validation.kind === 'units' ? (
                        <>
                          <TableHead><TableRow>{EXPECTED_HEADERS.map((header) => <TableCell key={header} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{header}</TableCell>)}</TableRow></TableHead>
                          <TableBody>
                            {validation.rows.slice(0, 5).map((row) => (
                              <TableRow key={row.projectName}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.projectName}</TableCell>
                                {[row.apartment, row.condominium, row.detachedHouse, row.executiveCondominium, row.semiDetachedHouse, row.terraceHouse, row.total]
                                  .map((value, index) => <TableCell key={index} align="right">{value.toLocaleString()}</TableCell>)}
                              </TableRow>
                            ))}
                          </TableBody>
                        </>
                      ) : (
                        <>
                          <TableHead><TableRow>{['Project Name', 'Company', 'Foreigner (NPR)', 'Permanent Residents (PR)', 'Singaporean', 'Total'].map((header) => <TableCell key={header} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{header}</TableCell>)}</TableRow></TableHead>
                          <TableBody>
                            {validation.rows.slice(0, 5).map((row) => (
                              <TableRow key={row.projectName}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.projectName}</TableCell>
                                {[row.company, row.foreigner, row.permanentResident, row.singaporean, row.total]
                                  .map((value, index) => <TableCell key={index} align="right">{value.toLocaleString()}</TableCell>)}
                              </TableRow>
                            ))}
                          </TableBody>
                        </>
                      )}
                    </Table>
                  </TableContainer>
                  <Button
                    variant="contained"
                    disabled={importing || Boolean(importResult)}
                    onClick={handleImport}
                    startIcon={importing ? <CircularProgress size={16} color="inherit" /> : undefined}
                    sx={{ mt: 2 }}
                  >
                    {importing
                      ? `Importing ${validation.kind === 'units' ? 'Units' : 'Nationality'}…`
                      : importResult
                        ? `${validation.kind === 'units' ? 'Units' : 'Nationality'} imported`
                        : `Import ${validation.kind === 'units' ? 'Units' : 'Nationality'}`}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
            </Paper>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
